import { LocaleLink as Link } from '../lib/locale-link.js';
import { useMarketingSeo } from '../lib/use-marketing-seo.js';
import { MarketingShell } from '../components/marketing-shell.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrialDays } from '../lib/use-trial-days.js';
import { useI18n } from '../lib/use-i18n.js';
import { RevealBlock } from '../components/reveal-block.js';

/**
 * /about — the "who we are" page. Three sections:
 *   1. Why this product exists (story / pain we noticed)
 *   2. What we believe (principles that shape the product)
 *   3. Where we are (Malaysia first, Southeast Asia next)
 *
 * Copy is mostly inline rather than from brand.copy because brand.copy
 * focuses on customer-facing pitch lines; an "about us" page is more
 * narrative + can drift per brand without breaking anything.
 */
export function AboutPage() {
  const brand = usePlatformBrand();
  const { t } = useI18n();
  useMarketingSeo({
    title: t('marketing.about.metaTitle', { brand: brand.name }),
    description: t('marketing.about.metaDescription', { brand: brand.name }),
  });
  return (
    <MarketingShell>
      <Hero name={brand.name} />
      <Story name={brand.name} />
      <Principles />
      <Geography />
      <BottomCta name={brand.name} />
    </MarketingShell>
  );
}

function Hero({ name }: { readonly name: string }) {
  const { t } = useI18n();
  return (
    <section
      style={{
        position: 'relative',
        // Same ambient wash as /features and the home hero — one site, one
        // visual system, at an amplitude that suits a secondary page.
        background:
          'radial-gradient(120% 90% at 50% -10%, rgba(var(--color-primary-rgb), 0.10), transparent 62%)',
        padding: 'var(--space-12) var(--space-6) var(--space-6) var(--space-6)',
        textAlign: 'center',
      }}
    >
      <RevealBlock style={{ maxWidth: 720, margin: '0 auto' }}>
        <p style={eyebrow}>{t('marketing.about.eyebrow')}</p>
        <h1
          style={{
            fontSize: 'var(--text-display)',
            lineHeight: 1.05,
            fontWeight: 700,
            margin: 'var(--space-2) 0 0 0',
            letterSpacing: '-0.02em',
          }}
        >
          {t('marketing.about.heroTitle')}
        </h1>
        <p
          style={{
            margin: 'var(--space-4) auto 0 auto',
            maxWidth: 560,
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t('marketing.about.heroSubtitle', { brand: name })}
        </p>
      </RevealBlock>
    </section>
  );
}

function Story({ name }: { readonly name: string }) {
  const { t } = useI18n();
  return (
    <section style={sectionStyle}>
      <RevealBlock style={{ maxWidth: 720, margin: '0 auto' }}>
        <p style={eyebrow}>{t('marketing.about.storyEyebrow')}</p>
        <h2 style={sectionH2}>{t('marketing.about.storyTitle')}</h2>
        <div
          style={{
            marginTop: 'var(--space-6)',
            display: 'grid',
            gap: 'var(--space-4)',
            fontSize: 'var(--text-body)',
            lineHeight: 1.7,
            color: 'var(--color-on-surface)',
          }}
        >
          <p>{t('marketing.about.storyP1')}</p>
          <p>{t('marketing.about.storyP2')}</p>
          <p>
            <strong>{name}</strong> {t('marketing.about.storyP3')}
          </p>
        </div>
      </RevealBlock>
    </section>
  );
}

interface Principle {
  readonly title: string;
  readonly body: string;
}

function Principles() {
  const brand = usePlatformBrand();
  const { t } = useI18n();
  const principles: readonly Principle[] = [
    {
      title: t('marketing.about.principle1Title'),
      body: t('marketing.about.principle1Body'),
    },
    {
      title: t('marketing.about.principle2Title'),
      body: t('marketing.about.principle2Body', { brand: brand.name }),
    },
    {
      title: t('marketing.about.principle3Title'),
      body: t('marketing.about.principle3Body'),
    },
    {
      title: t('marketing.about.principle4Title'),
      body: t('marketing.about.principle4Body'),
    },
  ];
  return (
    <section style={{ ...sectionStyle, background: 'var(--tint-subtle)' }}>
      <RevealBlock style={{ maxWidth: 1120, margin: '0 auto' }}>
        <p style={eyebrow}>{t('marketing.about.believeEyebrow')}</p>
        <h2 style={sectionH2}>{t('marketing.about.believeTitle')}</h2>
        <div
          style={{
            marginTop: 'var(--space-8)',
            display: 'grid',
            gap: 'var(--space-4)',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
          }}
        >
          {principles.map((p) => (
            <article
              key={p.title}
              style={{
                padding: 'var(--space-5)',
                borderRadius: 'var(--radius-lg)',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                boxShadow: 'var(--shadow-1)',
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 'var(--text-body)',
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
              >
                {p.title}
              </h3>
              <p
                style={{
                  margin: 'var(--space-2) 0 0 0',
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                  lineHeight: 1.5,
                }}
              >
                {p.body}
              </p>
            </article>
          ))}
        </div>
      </RevealBlock>
    </section>
  );
}

function Geography() {
  const { t } = useI18n();
  return (
    <section style={sectionStyle}>
      <RevealBlock style={{ maxWidth: 720, margin: '0 auto' }}>
        <p style={eyebrow}>{t('marketing.about.whereEyebrow')}</p>
        <h2 style={sectionH2}>{t('marketing.about.whereTitle')}</h2>
        <p
          style={{
            marginTop: 'var(--space-4)',
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.6,
          }}
        >
          {t('marketing.about.whereBody')}
        </p>
      </RevealBlock>
    </section>
  );
}

function BottomCta({ name }: { readonly name: string }) {
  const { t } = useI18n();
  const trialDays = useTrialDays();
  return (
    <section
      style={{
        padding: 'var(--space-12) var(--space-6) var(--space-16) var(--space-6)',
        textAlign: 'center',
      }}
    >
      <RevealBlock style={{ maxWidth: 560, margin: '0 auto' }}>
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--text-h1)',
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}
        >
          {t('marketing.about.bottomTitle')}
        </h2>
        <p
          style={{
            margin: 'var(--space-3) 0 0 0',
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t('marketing.about.bottomBody', { brand: name, days: trialDays })}
        </p>
        <div
          style={{
            marginTop: 'var(--space-6)',
            display: 'flex',
            gap: 'var(--space-3)',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Link to="/signup" style={ctaPrimary}>
            {t('marketing.cta.startTrial')}
          </Link>
          <Link to="/contact" style={ctaSecondary}>
            {t('marketing.about.ctaTalk')}
          </Link>
        </div>
      </RevealBlock>
    </section>
  );
}

const sectionStyle: React.CSSProperties = {
  padding: 'var(--space-12) var(--space-6)',
};
const sectionH2: React.CSSProperties = {
  margin: 'var(--space-2) 0 0 0',
  fontSize: 'var(--text-h1)',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  lineHeight: 1.2,
};
const eyebrow: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-primary)',
  letterSpacing: 3,
  textTransform: 'uppercase',
  fontWeight: 600,
  margin: 0,
};
const ctaPrimary: React.CSSProperties = {
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  padding: '14px 28px',
  borderRadius: 'var(--radius-pill)',
  fontWeight: 600,
  textDecoration: 'none',
  boxShadow: 'var(--shadow-2)',
};
const ctaSecondary: React.CSSProperties = {
  padding: '14px 28px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-strong)',
  color: 'var(--color-on-surface)',
  fontWeight: 500,
  textDecoration: 'none',
};
